import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { logger } from '@/services/logger'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import React from 'react'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error: Error; retry: () => void }> | React.ReactElement
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  showRetry?: boolean
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to centralized logger with component stack in the error
    const enrichedError = new Error(error.message)
    enrichedError.name = error.name
    if (error.stack !== undefined) {
      enrichedError.stack = error.stack
    }
    enrichedError.cause = { componentStack: errorInfo.componentStack }

    logger.error('Error Boundary caught an error', enrichedError)

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)
  }

  retry = () => {
    this.setState({ hasError: false, error: null })
  }

  override render() {
    if (this.state.hasError && this.state.error) {
      const { fallback, showRetry = true } = this.props

      // Handle custom fallback component
      if (fallback) {
        if (React.isValidElement(fallback)) {
          return fallback
        }
        if (typeof fallback === 'function') {
          const FallbackComponent = fallback
          return <FallbackComponent error={this.state.error} retry={this.retry} />
        }
      }

      // Default error UI
      return (
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle className="text-destructive">Something went wrong</CardTitle>
            <CardDescription>
              An unexpected error occurred. Please try refreshing the page.
            </CardDescription>
          </CardHeader>
          {showRetry && (
            <CardContent className="text-center">
              <Button onClick={this.retry} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          )}
        </Card>
      )
    }

    return this.props.children
  }
}
